import { useState, useEffect, useRef } from 'react';
import api from '../config/axios';
import { MdAdd, MdSearch, MdCheckCircle, MdPending, MdCancel, MdClose, MdDelete, MdVisibility, MdFilterList, MdError } from 'react-icons/md';
import { FaFilter } from "react-icons/fa6";


import { FaHourglass } from 'react-icons/fa';
import CreateSupportCaseModal from '../components/SupportCase/CreateSupportCaseModal';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const SupportCasesPage = () => {
  const { t } = useLanguage();
  const [supportCases, setSupportCases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [popup, setPopup] = useState({
    show: false,
    title: '',
    message: '',
    type: 'success',
    showCancel: false
  });
  
  // Column filters
  const [filters, setFilters] = useState({
    case_status: '',
    case_id: '',
    customer_name: '',
    subject: '',
    created_by_name: '',
    created_at: '',
    severity: '',
    assigned_to_name: ''
  });
  
  const filterRefs = useRef({});
  
  const navigate = useNavigate();
  const location = useLocation();
  
     useEffect(() => {
     // Dashboard'dan gelen filtreleme durumunu kontrol et
     if (location.state?.filter && location.state?.showToast) {
       
       const userInfo = JSON.parse(localStorage.getItem('user'));
       const userId = userInfo?.id;
       
       // Filtreleme uygula
       if (location.state.filterType === 'owner') {
         // Kullanıcıya atanan tüm kayıtları filtrele
         fetchAssignedCases(userId);
         // Filtre state'ini güncelle
         setFilters(prev => ({ ...prev, assigned_to_name: 'Bana Atanan' }));
       } else if (location.state.filterType === 'owner_status') {
         // Kullanıcıya atanan ve belirli durumda olan kayıtları filtrele
         fetchAssignedCasesWithStatus(userId, location.state.filter);
         // Filtre state'ini güncelle
         setFilters(prev => ({ 
           ...prev, 
           assigned_to_name: t('supportCase.assignedToMe'),
           case_status: location.state.filter 
         }));
       } else if (location.state.filterType === 'status') {
         // Normal case_status filtrelemesi (tüm kayıtlar üzerinde)
         handleFilterChange('case_status', location.state.filter);
       } else if (location.state.filterType === 'all') {
         // Tüm kayıtları getir (filtreleme yapma)
         fetchSupportCases();
       }
       
       // Toast mesajını göster (eğer varsa)
       if (location.state.toastMessage) {
         showToast(location.state.toastMessage, 'success');
       }
       
       // State'i temizle
       navigate(location.pathname, { replace: true });
     } else if (!location.state) {
       // Dashboard'dan filtre gelmiyorsa tüm kayıtları getir
       fetchSupportCases();
     }
   }, [location.state]);
  
  // Handle clicks outside filter dropdowns
  useEffect(() => {
    function handleClickOutside(event) {
      // Close all filter dropdowns if clicked outside
      Object.keys(filters).forEach(field => {
        const element = document.getElementById(`filter-${field}`);
        if (element && !element.contains(event.target) && 
            !event.target.closest(`button[data-filter="${field}"]`)) {
          element.style.display = 'none';
        }
      });
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [filters]);
  
  const toggleFilter = (field) => {
    // Close all other filters
    Object.keys(filters).forEach(key => {
      if (key !== field) {
        const element = document.getElementById(`filter-${key}`);
        if (element) {
          element.style.display = 'none';
        }
      }
    });
    
    // Toggle the selected filter
    const element = document.getElementById(`filter-${field}`);
    if (element) {
      element.style.display = element.style.display === 'none' ? 'block' : 'none';
    }
  };
  
  // Kullanıcıya atanan kayıtları getiren fonksiyon
  const fetchAssignedCases = async (userId) => {
    setIsLoading(true);
    try {
      const response = await api.get(
        `/support-cases?owner_id=${userId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setSupportCases(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching assigned cases:', error);
      setIsLoading(false);
    }
  };

  // Kullanıcıya atanan ve belirli durumda olan kayıtları getiren fonksiyon
  const fetchAssignedCasesWithStatus = async (userId, status) => {
    setIsLoading(true);
    try {
      const response = await api.get(
        `/support-cases?owner_id=${userId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      // API'den gelen kayıtları duruma göre filtrele
      const filteredCases = response.data.filter(caseItem => {
        const caseStatus = caseItem.case_status?.toLowerCase() || '';
        const targetStatus = status.toLowerCase();
        
        // Esnek durum eşleştirmesi
        if (targetStatus === 'in progress') {
          return caseStatus.includes('in progress') || caseStatus.includes('in_progress');
        } else if (targetStatus === 'closed') {
          return caseStatus.includes('closed');
        } else if (targetStatus === 'open') {
          return caseStatus.includes('open');
        }
        
        return caseStatus.includes(targetStatus);
      });
      
      setSupportCases(filteredCases);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching assigned cases with status:', error);
      setIsLoading(false);
    }
  };

  const fetchSupportCases = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/support-cases', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setSupportCases(response.data);
    } catch (error) {
      console.error('Error fetching support cases:', error);
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        navigate('/admin', { state: { message: t('messages.sessionEnded') } });
      } else {
      showPopup({
        title: 'Hata',
        message: t('messages.supportCaseFetchError'),
        type: 'error'
      });
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleCaseClick = (caseItem) => {
    // UUID varsa UUID kullan, yoksa ID kullan (geriye uyumluluk için)
    const identifier = caseItem.support_case_uuid || caseItem.id;
    navigate(`/support-cases/${identifier}`);
  };
  
  const handleDeleteCase = (caseId, e) => {
    e.stopPropagation(); // Prevent triggering the row click
    
    showPopup({
      title: t('messages.confirmation'),
      message: t('messages.confirmDeleteSupportTicket'),
      type: 'warning',
      showCancel: true,
      onConfirm: async () => {
        try {
          await api.delete(`/support-cases/${caseId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          showPopup({
            title: t('messages.success'),
            message: t('messages.supportTicketDeleted'),
            type: 'success'
          });
          
          // Refresh the support cases list
          fetchSupportCases();
        } catch (error) {
          console.error('Error deleting support case:', error);
          showPopup({
            title: t('messages.error'),
            message: t('messages.errorDeletingSupportTicket'),
            type: 'error'
          });
        }
      }
    });
  };
  
  const handleCreateCase = async (newCase) => {
    try {
      // Backend artık case ID'yi özel tarihe göre oluşturuyor
      const response = await api.post('/support-cases', newCase, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setIsCreateModalOpen(false);
      fetchSupportCases();
      
      showPopup({
        title: t('messages.success'),
        message: t('supportCase.supportCaseCreated'),
        type: 'success'
      });
    } catch (error) {
      console.error('Error creating support case:', error);
      showPopup({
        title: t('messages.error'),
        message: t('supportCase.supportCaseError'),
        type: 'error'
      });
    }
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'OPEN':
        return <MdError className="text-red-500" size={20} />;
      case 'IN_PROGRESS':
        return <FaHourglass className="text-[#232b3b]" size={18} />;
      case 'RESOLVED':
        return <MdCheckCircle className="text-green-500" size={20} />;
      case 'CLOSED':
        return <MdCheckCircle className="text-green-500" size={20} />;
      default:
        return <MdPending className="text-yellow-500" size={20} />;
    }
  };
  
  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1); // Reset to first page when filter changes
  };
  
  // Clear all filters
  const clearFilters = () => {
    setFilters({
      case_status: '',
      case_id: '',
      customer_name: '',
      subject: '',
      created_by_name: '',
      created_at: '',
      severity: '',
      assigned_to_name: ''
    });
    setSearchTerm('');
    
         // Dashboard'dan gelen filtre varsa temizle ve tüm kayıtları getir
     if (location.state?.filter && location.state?.showToast && (location.state?.filterType === 'owner' || location.state?.filterType === 'owner_status')) {
       navigate(location.pathname, { replace: true });
       fetchSupportCases();
     }
  };
  
  const filteredCases = supportCases.filter(caseItem => {
    if (!caseItem) return false;
    
    // Global search term filter
    const matchesSearchTerm = searchTerm === '' || 
      (caseItem.case_id && caseItem.case_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (caseItem.subject && caseItem.subject.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (caseItem.customer_name && caseItem.customer_name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    if (!matchesSearchTerm) return false;
    
         // Dashboard'dan gelen filtre varsa sadece search term filtrelemesi yap
     if (location.state?.filter && location.state?.showToast && (location.state?.filterType === 'owner' || location.state?.filterType === 'owner_status')) {
       return true; // Dashboard filtresi zaten uygulanmış, sadece search term kontrol et
     }
    
    // Column-specific filters (sadece dashboard filtresi yoksa)
    const matchesStatus = filters.case_status === '' || 
      (caseItem.case_status && caseItem.case_status.toLowerCase().includes(filters.case_status.toLowerCase()));
    
    // Debug logging for status filtering
    if (filters.case_status !== '') {
    }
    
    const matchesCaseId = filters.case_id === '' || 
      (caseItem.case_id && caseItem.case_id.toLowerCase().includes(filters.case_id.toLowerCase()));
    
    const matchesCustomer = filters.customer_name === '' || 
      (caseItem.customer_name && caseItem.customer_name.toLowerCase().includes(filters.customer_name.toLowerCase()));
    
    const matchesSubject = filters.subject === '' || 
      (caseItem.subject && caseItem.subject.toLowerCase().includes(filters.subject.toLowerCase()));
    
    const matchesCreator = filters.created_by_name === '' || 
      (caseItem.created_by_name && caseItem.created_by_name.toLowerCase().includes(filters.created_by_name.toLowerCase()));
    
    const matchesDate = filters.created_at === '' || 
      (caseItem.created_at && new Date(caseItem.created_at).toLocaleString().includes(filters.created_at));
    
    const matchesSeverity = filters.severity === '' || 
      (caseItem.severity && caseItem.severity.toLowerCase().includes(filters.severity.toLowerCase()));
    
    const matchesAssignee = filters.assigned_to_name === '' || 
      ((caseItem.assigned_to_name || t('supportCase.notAssignedYet')).toLowerCase().includes(filters.assigned_to_name.toLowerCase()));
    
    return matchesStatus && matchesCaseId && matchesCustomer && matchesSubject && 
           matchesCreator && matchesDate && matchesSeverity && matchesAssignee;
  });
  
  // Pagination logic
  const pageSizeOptions = [10, 20, 50, 100, 500];
  const indexOfLastCase = currentPage * itemsPerPage;
  const indexOfFirstCase = indexOfLastCase - itemsPerPage;
  const currentCases = filteredCases.slice(indexOfFirstCase, indexOfLastCase);
  const totalPages = Math.ceil(filteredCases.length / itemsPerPage);
  
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  const handlePageSizeChange = (newSize) => {
    setItemsPerPage(Number(newSize));
    setCurrentPage(1); // Reset to first page when changing page size
  };
  
  // Popup management
  const showPopup = (options) => {
    setPopup({
      show: true,
      title: options.title || 'Notification',
      message: options.message,
      type: options.type || 'success',
      showCancel: options.showCancel ?? false,
      onConfirm: options.onConfirm || null
    });
  };
  
  const closePopup = () => {
    setPopup(prev => ({ ...prev, show: false }));
  };
  
  const confirmPopup = () => {
    if (popup.onConfirm) {
      popup.onConfirm();
    }
    closePopup();
  };

     // Toast mesajı gösterme fonksiyonu
   const showToast = (message, type = 'success') => {
     // Toast mesajı için geçici bir div oluştur
     const toast = document.createElement('div');
     toast.className = `fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg transition-all duration-300 transform translate-x-full ${
       type === 'success' ? 'bg-gradient-to-br from-blue-500 to-blue-900 text-white' : 'bg-red-500 text-white'
     }`;
     toast.textContent = message;
    
    document.body.appendChild(toast);
    
    // Animasyon için setTimeout kullan
    setTimeout(() => {
      toast.classList.remove('translate-x-full');
    }, 100);
    
    // 3 saniye sonra kaldır
    setTimeout(() => {
      toast.classList.add('translate-x-full');
      setTimeout(() => {
        if (document.body.contains(toast)) {
          document.body.removeChild(toast);
        }
      }, 300);
    }, 3000);
  };
  
  return (
    <div className="h-full overflow-auto">
      <div className="flex justify-between items-center gap-3 mb-6">
        <h1 className="text-xl sm:text-2xl font-medium text-gray-800 dark:text-white">{t('supportCase.supportCases')}</h1>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="flex items-center justify-center gap-2 px-3 py-2.5 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230] transition-colors w-11 h-10 sm:w-auto sm:h-auto sm:px-6 sm:py-3 -ml-2.5 sm:ml-0"
        >
          <MdAdd size={20} className="sm:hidden" />
          <MdAdd size={24} className="hidden sm:block" />
          <span className="hidden sm:inline">{t('supportCase.newSupportTicket')}</span>
        </button>
      </div>

      {/* Total Count */}
      <div className="mb-4">
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {t('supportCase.totalCases')}: {filteredCases.length}
        </span>
      </div>
      
      <div className="mb-6 relative">
        <input
          type="text"
                        placeholder={t('supportCase.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 pl-10 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <MdSearch className="absolute left-3 top-2.5 text-gray-400 dark:text-gray-500" size={20} />
      </div>
      
      {/* Filter reset button */}
      {Object.values(filters).some(filter => filter !== '') && (
        <div className="mb-4">
          <button 
            onClick={clearFilters}
            className="px-3 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 text-sm flex items-center gap-1"
          >
            <MdClose size={16} />
                          {t('common.clear')}
          </button>
        </div>
      )}
      
      {/* Support Cases Table */}
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 ${totalPages <= 1 ? 'rounded-b-lg' : ''}`}>
        {/* Page size selector */}
        <div className="px-3 sm:px-6 py-3 border-b border-gray-200 dark:border-[#565c66] bg-gray-50 dark:bg-gray-700 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 rounded-tl-lg rounded-tr-lg">
          <div className="flex items-center gap-2 text-xs sm:text-sm">
            <span className="text-sm text-gray-600 dark:text-gray-400">{t('common.show')}</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handlePageSizeChange(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span className="text-sm text-gray-600 dark:text-gray-400">{t('supportCase.records')}</span>
          </div>
        </div>
        
        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto overflow-y-hidden">
          <table className="w-full min-w-[800px] table-auto">
                      <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
            <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 shadow-sm">
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">
                  <div className="flex items-center gap-2 relative">
                    {t('supportCase.status')}
                    <button 
                      onClick={() => toggleFilter('case_status')}
                      className="text-gray-400 hover:text-gray-600"
                                              title={t('supportCase.filterStatus')}
                      data-filter="case_status"
                    >
                      <FaFilter size={12} className={filters.case_status ? "text-blue-500" : ""} />
                    </button>
                    <div 
                      id="filter-case_status" 
                      className="absolute mt-8 bg-white shadow-lg rounded-md p-2 z-20 border border-gray-200 top-full left-0 w-36" 
                      style={{display: 'none'}}
                    >
                      <div className="flex flex-col gap-1">
                        <button 
                          onClick={() => handleFilterChange('case_status', '')}
                          className={`px-2 py-1 text-sm text-left rounded hover:bg-gray-100 ${filters.case_status === '' ? 'bg-gray-100 font-medium' : ''}`}
                        >
                          {t('supportCase.all')}
                        </button>
                        <button 
                          onClick={() => handleFilterChange('case_status', 'OPEN')}
                          className={`px-2 py-1 text-sm text-left rounded hover:bg-gray-100 ${filters.case_status === 'OPEN' ? 'bg-gray-100 font-medium' : ''}`}
                        >
                          OPEN
                        </button>
                        <button 
                          onClick={() => handleFilterChange('case_status', 'IN PROGRESS')}
                          className={`px-2 py-1 text-sm text-left rounded hover:bg-gray-100 ${filters.case_status === 'IN PROGRESS' ? 'bg-gray-100 font-medium' : ''}`}
                        >
                          IN PROGRESS
                        </button>
                  
                        <button 
                          onClick={() => handleFilterChange('case_status', 'CLOSED')}
                          className={`px-2 py-1 text-sm text-left rounded hover:bg-gray-100 ${filters.case_status === 'CLOSED' ? 'bg-gray-100 font-medium' : ''}`}
                        >
                          CLOSED
                        </button>
                      </div>
                    </div>
                  </div>
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">
                  <div className="flex items-center gap-2">
                                          {t('supportCase.caseId')}
                    <button 
                      onClick={() => toggleFilter('case_id')}
                      className="text-gray-400 hover:text-gray-600"
                                              title={t('supportCase.filterCaseId')}
                      data-filter="case_id"
                    >
                      <FaFilter size={12} className={filters.case_id ? "text-blue-500" : ""} />
                    </button>
                    <div 
                      id="filter-case_id" 
                      className="absolute mt-8 bg-white shadow-lg rounded-md p-2 z-20 border border-gray-200" 
                      style={{display: 'none'}}
                    >
                      <input
                        type="text"
                                                  placeholder={t('supportCase.filterCaseIdPlaceholder')}
                        value={filters.case_id}
                        onChange={(e) => handleFilterChange('case_id', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">
                  <div className="flex items-center gap-2">
                                          {t('supportCase.customer')}
                    <button 
                      onClick={() => toggleFilter('customer_name')}
                      className="text-gray-400 hover:text-gray-600"
                                              title={t('supportCase.filterCustomer')}
                      data-filter="customer_name"
                    >
                      <FaFilter size={12} className={filters.customer_name ? "text-blue-500" : ""} />
                    </button>
                    <div 
                      id="filter-customer_name" 
                      className="absolute mt-8 bg-white shadow-lg rounded-md p-2 z-20 border border-gray-200" 
                      style={{display: 'none'}}
                    >
                      <input
                        type="text"
                                                  placeholder={t('supportCase.filterCustomerPlaceholder')}
                        value={filters.customer_name}
                        onChange={(e) => handleFilterChange('customer_name', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">
                  <div className="flex items-center gap-2">
                                          {t('supportCase.subject')}
                    <button 
                      onClick={() => toggleFilter('subject')}
                      className="text-gray-400 hover:text-gray-600"
                                              title={t('supportCase.filterSubject')}
                      data-filter="subject"
                    >
                      <FaFilter size={12} className={filters.subject ? "text-blue-500" : ""} />
                    </button>
                    <div 
                      id="filter-subject" 
                      className="absolute mt-8 bg-white shadow-lg rounded-md p-2 z-20 border border-gray-200" 
                      style={{display: 'none'}}
                    >
                      <input
                        type="text"
                                                  placeholder={t('supportCase.filterSubjectPlaceholder')}
                        value={filters.subject}
                        onChange={(e) => handleFilterChange('subject', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">
                  <div className="flex items-center gap-2">
                                          {t('supportCase.createdBy')}
                    <button 
                      onClick={() => toggleFilter('created_by_name')}
                      className="text-gray-400 hover:text-gray-600"
                                              title={t('supportCase.filterCreatedBy')}
                      data-filter="created_by_name"
                    >
                      <FaFilter size={12} className={filters.created_by_name ? "text-blue-500" : ""} />
                    </button>
                    <div 
                      id="filter-created_by_name" 
                      className="absolute mt-8 bg-white shadow-lg rounded-md p-2 z-20 border border-gray-200" 
                      style={{display: 'none'}}
                    >
                      <input
                        type="text"
                                                  placeholder={t('supportCase.filterCreatedByPlaceholder')}
                        value={filters.created_by_name}
                        onChange={(e) => handleFilterChange('created_by_name', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">
                  <div className="flex items-center gap-2">
                    {t('supportCase.creationDate')}
                    <button 
                      onClick={() => toggleFilter('created_at')}
                      className="text-gray-400 hover:text-gray-600"
                                              title={t('supportCase.filterDate')}
                      data-filter="created_at"
                    >
                      <FaFilter size={12} className={filters.created_at ? "text-blue-500" : ""} />
                    </button>
                    <div 
                      id="filter-created_at" 
                      className="absolute mt-8 bg-white shadow-lg rounded-md p-2 z-20 border border-gray-200" 
                      style={{display: 'none'}}
                    >
                      <input
                        type="text"
                                                  placeholder={t('supportCase.filterDatePlaceholder')}
                        value={filters.created_at}
                        onChange={(e) => handleFilterChange('created_at', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">
                  <div className="flex items-center gap-2">
                    {t('supportCase.priority')}
                    <button 
                      onClick={() => toggleFilter('severity')}
                      className="text-gray-400 hover:text-gray-600"
                                              title={t('supportCase.filterPriority')}
                      data-filter="severity"
                    >
                      <FaFilter size={12} className={filters.severity ? "text-blue-500" : ""} />
                    </button>
                    <div 
                      id="filter-severity" 
                      className="absolute mt-8 bg-white shadow-lg rounded-md p-2 z-20 border border-gray-200" 
                      style={{display: 'none'}}
                    >
                      <input
                        type="text"
                                                  placeholder={t('supportCase.filterPriorityPlaceholder')}
                        value={filters.severity}
                        onChange={(e) => handleFilterChange('severity', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">
                  <div className="flex items-center gap-2">
                                          {t('supportCase.assignedTo')}
                    <button 
                      onClick={() => toggleFilter('assigned_to_name')}
                      className="text-gray-400 hover:text-gray-600"
                                              title={t('supportCase.filterAssignedTo')}
                      data-filter="assigned_to_name"
                    >
                      <FaFilter size={12} className={filters.assigned_to_name ? "text-blue-500" : ""} />
                    </button>
                    <div 
                      id="filter-assigned_to_name" 
                      className="absolute mt-8 bg-white shadow-lg rounded-md p-2 z-20 border border-gray-200" 
                      style={{display: 'none'}}
                    >
                      <input
                        type="text"
                                                  placeholder={t('supportCase.filterAssignedToPlaceholder')}
                        value={filters.assigned_to_name}
                        onChange={(e) => handleFilterChange('assigned_to_name', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700 rounded-bl-lg rounded-br-lg">
              {isLoading ? (
                <tr>
                  <td colSpan="8" className="py-4 px-4 text-center text-gray-500 dark:text-gray-400 text-sm rounded-bl-lg rounded-br-lg">{t('supportCase.loadingCases')}</td>
                </tr>
              ) : currentCases.length === 0 ? (
                <tr>
                  <td colSpan="8" className="py-4 px-4 text-center text-gray-500 dark:text-gray-400 text-sm rounded-bl-lg rounded-br-lg">{t('supportCase.noCasesFound')}</td>
                </tr>
              ) : (
                currentCases.map((caseItem, index) => {
                  const isLast = index === currentCases.length - 1 && totalPages <= 1;
                  return (
                    <tr 
                      key={caseItem.id} 
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer`}
                      onClick={() => handleCaseClick(caseItem)}
                    >
                      {/* İlk hücre: Eğer son satırsa sol alt köşe yuvarlatılır */}
                      <td className={`px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white ${isLast ? 'rounded-bl-lg' : ''}`}>
                        <div className="flex items-center">
                          {getStatusIcon(caseItem.case_status)}
                          <span className="ml-2">{caseItem.case_status}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">{caseItem.case_id}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white relative group">
                        <span>
                          {caseItem.customer_name.length > 20 
                            ? caseItem.customer_name.substring(0, 20) + '...' 
                            : caseItem.customer_name}
                        </span>
                        {caseItem.customer_name.length > 20 && (
                          <div className="absolute z-10 invisible group-hover:visible bg-gray-800 dark:bg-gray-600 text-white dark:text-gray-200 text-xs rounded py-2 px-3 left-0 top-full mt-1 min-w-max max-w-xs whitespace-normal break-words shadow-lg">
                            {caseItem.customer_name}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white relative group">
                        <span>
                          {caseItem.subject.length > 20 
                            ? caseItem.subject.substring(0, 20) + '...' 
                            : caseItem.subject}
                        </span>
                        {caseItem.subject.length > 20 && (
                          <div className="absolute z-10 invisible group-hover:visible bg-gray-800 dark:bg-gray-600 text-white dark:text-gray-200 text-xs rounded py-2 px-3 left-0 top-full mt-1 min-w-max max-w-xs whitespace-normal break-words shadow-lg">
                            {caseItem.subject}
                        </div>
                        )}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">{caseItem.created_by_name}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">{new Date(caseItem.created_at).toLocaleString()}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white"> 
                        <span>
                          {caseItem.severity.length > 20 
                            ? caseItem.severity.substring(0, 20) + '...' 
                            : caseItem.severity}
                        </span>
                        {caseItem.severity.length > 20 && (
                          <div className="absolute z-10 invisible group-hover:visible bg-gray-800 dark:bg-gray-600 text-white dark:text-gray-200 text-xs rounded py-2 px-3 left-0 top-full mt-1 min-w-max max-w-xs whitespace-normal break-words shadow-lg">
                            {caseItem.severity}
                          </div>
                        )}
                      </td>
                      {/* Son hücre: Eğer son satırsa sağ alt köşe yuvarlatılır */}
                      <td className={`px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white ${isLast ? 'rounded-br-lg' : ''}`}>{caseItem.assigned_to_name || t('supportCase.notAssignedYet')}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden space-y-4 p-4">
          {isLoading ? (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">{t('supportCase.loadingCases')}</div>
          ) : currentCases.length === 0 ? (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">{t('supportCase.noCasesFound')}</div>
          ) : (
            currentCases.map((caseItem, index) => (
              <div 
                key={caseItem.id} 
                className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                onClick={() => handleCaseClick(caseItem)}
              >
                {/* Status & Case ID */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('supportCase.status')}</div>
                    <div className="flex items-center">
                      {getStatusIcon(caseItem.case_status)}
                      <span className="ml-2 text-sm text-gray-900 dark:text-white">{caseItem.case_status}</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('supportCase.caseId')}</div>
                    <div className="text-sm text-gray-900 dark:text-white font-medium">{caseItem.case_id}</div>
                  </div>
                </div>

                {/* Customer & Subject */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('supportCase.customer')}</div>
                    <div className="text-sm text-gray-900 dark:text-white">{caseItem.customer_name}</div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('supportCase.subject')}</div>
                    <div className="text-sm text-gray-900 dark:text-white">{caseItem.subject}</div>
                  </div>
                </div>

                {/* Created By & Creation Date */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('supportCase.createdBy')}</div>
                    <div className="text-sm text-gray-900 dark:text-white">{caseItem.created_by_name}</div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('supportCase.creationDate')}</div>
                    <div className="text-sm text-gray-900 dark:text-white">{new Date(caseItem.created_at).toLocaleString()}</div>
                  </div>
                </div>

                {/* Priority & Assigned To */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('supportCase.priority')}</div>
                    <div className="text-sm text-gray-900 dark:text-white">{caseItem.severity}</div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('supportCase.assignedTo')}</div>
                    <div className="text-sm text-gray-900 dark:text-white">{caseItem.assigned_to_name || t('supportCase.notAssignedYet')}</div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6 rounded-b-lg">
            <div className="flex flex-1 items-center justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {t('table.showing')}{' '}
                  <span className="font-medium">{indexOfFirstCase + 1}</span>
                  {' '}{t('table.to')}{' '}
                  <span className="font-medium">
                    {Math.min(indexOfLastCase, filteredCases.length)}
                  </span>{' '}
                  {t('table.of')}{' '}
                  <span className="font-medium">{filteredCases.length}</span>{' '}
                  {t('supportCase.supportCases').toLowerCase()}
                </p>
              </div>
              <div>
                <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-medium ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    {t('common.previous')}
                  </button>
                  {/* Mobilde sadece yakın sayfaları göster */}
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNum = index + 1;
                    const showPage = pageNum === 1 || 
                                   pageNum === totalPages || 
                                   Math.abs(pageNum - currentPage) <= 1;
                    
                    if (!showPage && pageNum === currentPage - 2) {
                      return <span key={pageNum} className="px-1 text-gray-500">...</span>;
                    }
                    
                    if (!showPage) return null;
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-3 sm:px-4 py-2 text-sm font-medium ${
                          currentPage === pageNum
                            ? 'bg-[#272c39] text-white'
                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        <span className="hidden sm:inline">{pageNum}</span>
                        <span className="sm:hidden">{pageNum}</span>
                      </button>
                    );
                  })}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center rounded-r-md px-3 py-2 text-sm font-medium ${
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    {t('common.next')}
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Create Support Case Modal */}
      {isCreateModalOpen && (
        <CreateSupportCaseModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={handleCreateCase}
        />
      )}
      
      {/* Success/Error Popup */}
      {popup.show && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999] flex items-center justify-center">
          <div className="relative mx-auto p-8 border w-[500px] shadow-lg rounded-md bg-white">
            <div className="mt-4 text-center">
              <h3 className="text-xl leading-6 font-medium text-gray-900 mb-4">
                {popup.title}
              </h3>
              <div className="mt-4 px-8 py-4">
                <p className="text-base text-gray-600">
                  {popup.message}
                </p>
              </div>
              <div className="flex justify-center gap-4 px-8 py-4 mt-2">
                {popup.showCancel && (
                  <button
                    onClick={closePopup}
                    className="px-6 py-2.5 bg-gray-200 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-300 focus:outline-none min-w-[120px]"
                  >
                    {t('messages.cancel')}
                  </button>
                )}
                                  <button
                    onClick={confirmPopup}
                    className={`px-6 py-2.5 text-white text-base font-medium rounded-md shadow-sm focus:outline-none min-w-[120px] ${
                      popup.type === 'error' 
                        ? 'bg-red-600 hover:bg-red-700'
                        : popup.type === 'success'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-[#272c39] hover:bg-[#1e2230]'
                    }`}
                  >
                    {t('messages.ok')}
                  </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportCasesPage; 